// Court-level mapping utility.
//
// Maps a judgment's stored `court_level` string onto the three-way court-level
// selector used on the Discovery page. The mapping is intentionally conservative:
// any court_level we don't explicitly recognize falls back to COUNTY_CIRCUIT
// (the most common post-judgment forum) and sets `isRecognized=false` so the
// UI can surface a "pick manually" warning.
//
// IMPORTANT: Keep COURT_LEVEL_KEYS in lockstep with the COURT_LEVELS array in
// /app/frontend/src/pages/discovery/DiscoveryPage.jsx. If a 4th level is ever
// added (e.g. "appellate"), update both.

export const COURT_LEVEL_KEYS = {
  SMALL_CLAIMS: "small_claims",
  COUNTY_CIRCUIT: "county_circuit",
  FEDERAL: "federal",
};

const EXACT_MATCHES = {
  "small claims court": COURT_LEVEL_KEYS.SMALL_CLAIMS,
  "county court": COURT_LEVEL_KEYS.COUNTY_CIRCUIT,
  "circuit court": COURT_LEVEL_KEYS.COUNTY_CIRCUIT,
  // State district courts generally handle County/Circuit-equivalent civil cases.
  "district court": COURT_LEVEL_KEYS.COUNTY_CIRCUIT,
  "federal court": COURT_LEVEL_KEYS.FEDERAL,
};

/**
 * Map a judgment's raw `court_level` string to a Discovery selector key.
 *
 * @param {string|null|undefined} judgmentCourtLevel - raw judgment.court_level value
 * @returns {{ key: string, isRecognized: boolean, isDefault: boolean }}
 *   - key: one of COURT_LEVEL_KEYS values. Always safe to pass to the selector.
 *   - isRecognized: true when the input matched an explicit rule.
 *     Callers use this to show the "unknown court level" warning strip.
 *   - isDefault: true when the mapper returned COUNTY_CIRCUIT as a fallback
 *     (i.e. input was empty OR unrecognized). Useful to drive the
 *     "Reset to judgment default" link visibility.
 */
export function mapJudgmentCourtLevelToDiscovery(judgmentCourtLevel) {
  const normalized = String(judgmentCourtLevel || "").trim().toLowerCase();

  if (!normalized) {
    return {
      key: COURT_LEVEL_KEYS.COUNTY_CIRCUIT,
      isRecognized: false,
      isDefault: true,
    };
  }

  if (EXACT_MATCHES[normalized]) {
    return {
      key: EXACT_MATCHES[normalized],
      isRecognized: true,
      isDefault: false,
    };
  }

  // Loose fallback: substring sniffing for free-text court_level values the
  // backend might not have normalized. We only match on clearly specific
  // tokens so "Superior Court of Alameda County" still routes correctly.
  if (normalized.includes("federal")) {
    return { key: COURT_LEVEL_KEYS.FEDERAL, isRecognized: true, isDefault: false };
  }
  if (normalized.includes("small claims")) {
    return { key: COURT_LEVEL_KEYS.SMALL_CLAIMS, isRecognized: true, isDefault: false };
  }
  if (
    normalized.includes("circuit") ||
    normalized.includes("county") ||
    normalized.includes("district") ||
    normalized.includes("superior")
  ) {
    return { key: COURT_LEVEL_KEYS.COUNTY_CIRCUIT, isRecognized: true, isDefault: false };
  }

  // Truly unrecognized — default + flag.
  return {
    key: COURT_LEVEL_KEYS.COUNTY_CIRCUIT,
    isRecognized: false,
    isDefault: true,
  };
}
